import { useEffect, useMemo, useState } from "react";
import { gaPricer, track } from "../lib/analytics";
import { WatchButton } from "../components/WatchButton";
import { WhatsAppBuyButton } from "../components/WhatsAppBuyButton";
import { ShippingEstimator } from "../components/ShippingEstimator";
import { ProductOffers } from "../market/ProductOffers";
import { marketApi } from "../market/api";
import { viewSource } from "../market/visitSource";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { CardFx } from "../components/CardFx";
import { Price } from "../components/Price";
import { TransferPrice } from "../components/TransferPrice";
import { ProductGrid } from "../components/ProductGrid";
import { QuantityStepper } from "../components/QuantityStepper";
import { Loading, ErrorMessage } from "../components/StatusMessage";
import { StockBadge } from "../components/StockBadge";
import { useCart } from "../cart/CartProvider";
import { maxQuantityFor } from "../cart/cartReducer";
import { useI18n } from "../i18n/I18nProvider";
import { useMoney } from "../lib/CurrencyProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError, api, imageUrl } from "../lib/api";
import { useCatalog, useComingSoon, useStoreSales } from "../lib/CatalogProvider";
import { useAsync } from "../lib/useAsync";
import { formatArs } from "../../shared/money";
import { MARKET_EXCLUDED_CATEGORIES, marketFiltersToQuery, OFFER_PARAM } from "../../shared/market";
import { formatReleaseDate } from "../../shared/banners";
import { rarityLabel } from "../../shared/rarity";
import { sealedContentsLines } from "../../shared/sealedContents";
import { packagingNote } from "../../shared/packaging";
import { CONDITION_LABEL, FINISH_LABEL, isCardCategory, LANGUAGE_LABEL, variantLabel } from "../../shared/variants";

/** The set's single cards on sale in the market (optionally one rarity). */
function setCardsLink(setNames: string[], rarity?: string): string {
  return `/mercado?${marketFiltersToQuery({ categories: ["singles"], sets: setNames, rarities: rarity ? [rarity] : [] })}`;
}

export function ProductPage() {
  const { slug = "" } = useParams();
  const location = useLocation();
  const { hash, search } = location;
  const navigate = useNavigate();
  const { t, pick, locale } = useI18n();
  const money = useMoney();
  const { format: formatPrice } = money;
  const { catalog } = useCatalog();
  const { add, open } = useCart();
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState<number | null>(null);
  const comingSoon = useComingSoon();
  // Closed, the store sells nothing of its own: the page is the card and the market's offers.
  const closed = !useStoreSales();
  // With the address it answered (`asked`): while the next card loads, the last one is still here.
  const result = useAsync(async (signal) => ({ asked: slug, ...(await api.product(slug, signal)) }), [slug]);
  const productId = result.data?.product.id ?? null;
  const productCategory = result.data?.product.category ?? null;
  const listable = productCategory !== null && !MARKET_EXCLUDED_CATEGORIES.includes(productCategory);
  // A link to one offer (?oferta=<id>, what a listing's address is now): it's in the table, marked.
  const offerParam = Number(new URLSearchParams(search).get(OFFER_PARAM));
  const highlight = Number.isInteger(offerParam) && offerParam > 0 ? offerParam : null;
  // Community offers for the same product (the table under the store's own offer).
  const offers = useAsync((signal) => (productId && listable ? marketApi.productSummary(productId, signal, highlight) : Promise.resolve(null)), [productId, listable, highlight]);
  // Where this visit came from, for the shops' stats (worked out once per card and offer).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const source = useMemo(() => viewSource(location.state, search), [slug, highlight]);
  // The linked offer, asked on its own: whether it's still for sale (and its visit, with where it came from).
  const linked = useAsync((signal) => (highlight ? marketApi.listing(highlight, signal, source) : Promise.resolve(null)), [highlight]);
  const off = highlight !== null && !linked.loading && (Boolean(linked.error) || (linked.data !== null && (linked.data.status !== "active" || linked.data.quantity < 1 || linked.data.vacationUntil !== null)));
  const gone = off && linked.data?.mine ? "mine" : off;
  // Each offer on the page is a visit for its shop's stats (listings have no page of their own); where it
  // came from is the linked offer's, the others were seen on the card's page.
  useEffect(() => {
    if (productId && listable) void marketApi.productSeen(productId, source, highlight).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, listable]);

  const product = result.data?.product;
  const setCards = result.data?.setCards ?? null;
  const contentsLines = sealedContentsLines(result.data?.contents, locale);
  const variants = product?.variants ?? [];

  // An old address (the product's slug changed): the API answered with the product, the URL becomes its own.
  useEffect(() => {
    // Only for the answer to this address: another card's page still on screen isn't a moved slug.
    if (product && result.data?.asked === slug && product.slug !== slug) navigate(`/producto/${encodeURIComponent(product.slug)}${search}${hash}`, { replace: true });
  }, [product, slug, search, hash, navigate]);

  // Default to the first variant (the API lists in-stock, cheapest first); reset per product.
  useEffect(() => {
    setQty(1);
    setVariantId(product?.variants?.[0]?.id ?? null);
  }, [slug, product]);
  // Analytics: product views.
  useEffect(() => {
    // Under an old address the page is about to move (and load again): one view, counted there.
    if (!product || product.slug !== slug) return;
    const ga = gaPricer(money);
    const price = ga.price(product.price, product.priceArs);
    track("view_item", { currency: ga.currency, value: price, items: [{ item_id: product.sku, item_name: product.name.es, price, quantity: 1, item_category: product.category }] });
  }, [product]);
  // Arriving from a search tile's "Ver ofertas" (#ofertas): go to the table once it's there.
  useEffect(() => {
    if (hash === "#ofertas" && offers.data) document.getElementById("ofertas")?.scrollIntoView?.({ behavior: "smooth" });
  }, [hash, offers.data]);
  useEffect(() => {
    document.title = product ? `${pick(product.name)} | pokefans.fun` : "pokefans.fun";
    return () => {
      document.title = "pokefans.fun";
    };
  }, [product, pick]);

  // The next card loading: not the last one's page under its address.
  if (!result.error && (!result.data || result.data.asked !== slug)) return <Loading />;
  if (result.error) {
    const notFound = result.error instanceof ApiError && result.error.status === 404;
    return (
      <div className="container section">
        <ErrorMessage message={notFound ? t("product.notFound") : undefined} onRetry={notFound ? undefined : result.reload} />
        <Link to="/mercado">← {t("product.backMarket")}</Link>
      </div>
    );
  }
  if (!product) return null;

  const name = pick(product.name);
  const selected = variants.find((v) => v.id === variantId) ?? variants[0] ?? null;
  const selectedStock = selected?.stock ?? 0;
  const category = catalog.categories.find((c) => c.slug === product.category);
  const soldOut = selectedStock <= 0;
  const printLangKey = `printLang.${product.language}` as MessageKey;

  return (
    <div className="container section">
      <nav className="breadcrumbs" aria-label="breadcrumbs">
        <Link to="/mercado">{t("nav.market")}</Link>
        {category && (
          <>
            {" / "}
            <Link to={`/mercado?${marketFiltersToQuery({ categories: [category.slug] })}`}>{pick(category.name)}</Link>
          </>
        )}
      </nav>

      <div className="product-detail">
        <div className="product-media">
          {product.energyType ? (
            <div className="fx-host fx-square">
              <img src={imageUrl(product.imageKey)} alt={name} width={600} height={600} />
              <CardFx type={product.energyType} />
            </div>
          ) : (
            <img src={imageUrl(product.imageKey)} alt={name} width={600} height={600} />
          )}
        </div>
        <div className="product-info">
          <h1>{name}</h1>
          {closed ? (
            offers.data && offers.data.count > 0 ? (
              <div className="price-row">
                <span className="price-from">{t("product.from")}</span>
                <span className="price">
                  <span className="price-now">{formatArs(offers.data.minPriceArs ?? 0, locale)}</span>
                </span>
              </div>
            ) : (
              offers.data && <p className="muted">{t("closed.noOffers")}</p>
            )
          ) : (
            <>
              <Price price={selected?.price ?? product.price} priceArs={selected ? selected.priceArs : product.priceArs} compareAt={variants.length > 1 ? null : product.compareAtPrice} />
              <TransferPrice price={selected?.price ?? product.price} priceArs={selected ? selected.priceArs : product.priceArs} />
              <StockBadge stock={selectedStock} />
            </>
          )}
          {!closed && product.presale && (
            <p className="presale-note">
              <span className="tag tag-presale">{t("product.presale")}</span>{" "}
              {product.releaseDate ? t("product.presaleNote", { date: formatReleaseDate(product.releaseDate, locale) }) : t("product.presaleNoteNoDate")}
            </p>
          )}
          {!closed && variants.length > 1 && (
            <fieldset className="variant-picker">
              <legend>{t("product.version")}</legend>
              {variants.map((v) => (
                <label key={v.id} className={v.id === selected?.id ? "variant-option is-selected" : "variant-option"}>
                  <input
                    type="radio"
                    name="variant"
                    value={v.id}
                    checked={v.id === selected?.id}
                    onChange={() => {
                      setVariantId(v.id);
                      setQty(1);
                    }}
                  />
                  <span className="variant-name">{variantLabel(v, locale)}</span>
                  <span className="variant-meta">
                    <strong>{formatPrice(v.price, v.priceArs)}</strong>
                    <small className={v.stock > 0 ? "stock-in" : "stock-out"}>
                      {v.stock > 0 ? t("product.versionStock", { count: v.stock }) : t("product.outOfStock")}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
          <p className="product-description">{pick(product.description)}</p>
          {!closed && isCardCategory(product.category) && <p className="product-packaging">{packagingNote(selected?.price ?? product.price, locale)}</p>}
          {contentsLines.length > 0 && (
            <section className="product-contents" aria-labelledby="contents-title">
              <h2 id="contents-title">{t("product.contents")}</h2>
              <ul>
                {contentsLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </section>
          )}
          <dl className="product-meta">
            {product.setName && (
              <>
                <dt>{t("product.set")}</dt>
                <dd>
                  {product.setName}
                  {product.setCode ? ` (${product.setCode})` : ""}
                  {product.extraSets?.length ? `, ${product.extraSets.join(", ")}` : ""}
                </dd>
              </>
            )}
            {product.cardNumber && (
              <>
                <dt>{t("product.number")}</dt>
                <dd>{product.cardNumber}</dd>
              </>
            )}
            {product.rarity && (
              <>
                <dt>{t("product.rarity")}</dt>
                <dd>{rarityLabel(product.rarity, locale)}</dd>
              </>
            )}
            {!closed && selected && selected.condition !== "SEALED" && (
              <>
                <dt>{t("product.condition")}</dt>
                <dd>{CONDITION_LABEL[selected.condition][locale]}</dd>
                <dt>{t("product.finish")}</dt>
                <dd>{FINISH_LABEL[selected.finish][locale]}</dd>
              </>
            )}
            <dt>{t("product.language")}</dt>
            <dd>{selected && !closed ? (LANGUAGE_LABEL[selected.language]?.[locale] ?? selected.language.toUpperCase()) : t(printLangKey)}</dd>
            <dt>{t("product.sku")}</dt>
            <dd>{closed ? product.sku : (selected?.sku ?? product.sku)}</dd>
          </dl>
          {comingSoon && (
            <button type="button" className="btn btn-soon btn-lg" disabled>
              {t("soon.button")}
            </button>
          )}
          {!comingSoon && !closed && !soldOut && (
            <div className="buy-row">
              <QuantityStepper label={t("product.quantity")} value={qty} max={maxQuantityFor(selectedStock)} onChange={setQty} />
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => {
                  if (selected) add(product, selected, qty);
                  open();
                }}
              >
                {product.presale ? t("product.reserve") : t("product.addToCart")}
              </button>
            </div>
          )}
          {!comingSoon && !closed && soldOut && (
            <button type="button" className="btn btn-lg" disabled>
              {t("product.outOfStock")}
            </button>
          )}
          {!comingSoon && (soldOut || closed) && offers.data && offers.data.count > 0 && (
            <a href="#ofertas" className="offers-jump">
              {t("offers.jump", { count: offers.data.count, price: formatArs(offers.data.minPriceArs ?? 0, locale) })} ↓
            </a>
          )}
          {!comingSoon && !closed && (
            <WhatsAppBuyButton
              product={product}
              variant={selected}
              price={selected?.price ?? product.price}
              priceArs={selected ? selected.priceArs : product.priceArs}
              quantity={qty}
              soldOut={soldOut}
            />
          )}
          {!comingSoon && !closed && <ShippingEstimator />}
          {!comingSoon && !closed && <WatchButton product={product} />}
        </div>
      </div>

      {listable && <ProductOffers productId={product.id} data={offers.error ? undefined : (offers.data ?? undefined)} sealed={product.category !== "singles"} highlight={highlight} gone={gone} />}
      {listable && offers.data && (
        <p className="wanted-strip">
          🔎{" "}
          {offers.data.wanted ? (
            <Link to={`/se-busca/${encodeURIComponent(product.slug)}`}>{t("wanted.productStrip", { count: offers.data.wanted })}</Link>
          ) : (
            <Link to={`/se-busca/publicar?carta=${encodeURIComponent(product.slug)}`}>{t("wanted.productStripNone")}</Link>
          )}
        </p>
      )}

      {setCards && (
        <section className="section set-cards" aria-labelledby="set-cards-title">
          <h2 id="set-cards-title" className="section-title">
            {t("product.setCards")}
          </h2>
          <p className="muted">{(setCards.setNames?.length ?? 1) > 1
              ? t("product.setCardsIntroMany", { count: setCards.total, sets: setCards.setNames!.join(", ") })
              : t("product.setCardsIntro", { count: setCards.total, set: setCards.setName })}</p>
          {setCards.rarities.length > 0 && (
            <ul className="set-rarities" aria-label={t("product.rarity")}>
              {setCards.rarities.map((r) => (
                <li key={r.name}>
                  <Link to={setCardsLink(setCards.setNames ?? [setCards.setName], r.name)} className="rarity-tag">
                    {rarityLabel(r.name, locale) ?? r.name} · {r.count}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <ProductGrid products={setCards.top} />
          <p>
            <Link to={setCardsLink(setCards.setNames ?? [setCards.setName])} className="btn btn-outline">
              {t("product.setCardsAll")}
            </Link>
          </p>
        </section>
      )}

      {result.data && result.data.related.length > 0 && (
        <section className="section">
          <h2 className="section-title">{t("product.related")}</h2>
          <ProductGrid products={result.data.related} />
        </section>
      )}
    </div>
  );
}
